// Бейдж последней проверки цены на карточке рейса.
//
// Бейдж НИЧЕГО не утверждает о том, что цена изменилась: сравнение идёт по
// одному и тому же рейсу, но найденная цена — это цена конкретного сайта в
// конкретную минуту, а не новая цена билета. Поэтому формулировка всегда
// «на {site} сейчас {price}» плюс «проверено {when}», а не «подешевело».
import type { ReactElement } from 'react';
import type { FlightSegment } from '../types';
import { formatMoney } from '../core';
import { useT } from '../i18n';
import { useStore } from '../store';
import { usePrices } from './PricesProvider';
import { formatWhen, isStale, priceDelta } from './staleness';
import type { PriceDirection } from './staleness';
import styles from './PriceBadge.module.css';

export interface PriceBadgeProps {
  segment: FlightSegment;
  onOpenHistory(): void;
}

/** Стрелка вниз — нашли дешевле, вверх — дороже, черта — та же цена. */
function DirectionIcon({ direction }: { direction: PriceDirection }): ReactElement {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {direction === 'cheaper' && <path d="M12 5v14M6 13l6 6 6-6" />}
      {direction === 'dearer' && <path d="M12 19V5M6 11l6-6 6 6" />}
      {(direction === 'same' || direction === 'none') && <path d="M6 12h12" />}
    </svg>
  );
}

export function PriceBadge({ segment, onOpenHistory }: PriceBadgeProps): ReactElement | null {
  const { ready, checkOf } = usePrices();
  const { currencies } = useStore();
  const t = useT();

  const check = checkOf(segment.id);
  // Фича выключена или рейс ещё ни разу не проверяли — бейджу нечего сказать.
  if (!ready || check === null) return null;

  const now = Date.now();
  // Сравниваем с сегодняшней ценой блока, а не с той, что была записана при
  // проверке: иначе после «поставить эту цену» бейдж вечно звал бы к скидке,
  // которой уже нет. Валюта разошлась — остаёмся на серверной базе.
  const sameCurrency = segment.price.currency === check.baseCurrency;
  const delta = priceDelta(check, sameCurrency ? segment.price.amount : null);
  const stale = isStale(check.checkedAt, now);

  let text: string;
  if (check.status === 'ok' && check.bestMinor !== null) {
    const direction = delta.direction === 'none' ? 'same' : delta.direction;
    text = t(`prices.badge.${direction}`, {
      site: check.bestSite,
      price: formatMoney(check.bestMinor, check.baseCurrency, currencies),
    });
  } else if (check.status === 'not_found') {
    text = t('prices.badge.none');
  } else {
    // Сервер объясняет отказ по конкретному рейсу в note — это честнее общей
    // формулировки; своего текста нет только тогда, когда его не прислали.
    text = check.note || t('prices.err.unknown');
  }

  const when = t('prices.badge.checked', { when: formatWhen(check.checkedAt, now) });
  const percent =
    delta.direction === 'cheaper' || delta.direction === 'dearer'
      ? `${delta.percent > 0 ? '+' : '−'}${Math.abs(delta.percent)}%`
      : '';

  return (
    <button
      type="button"
      className={[styles.badge, styles[delta.direction], stale ? styles.stale : '']
        .filter(Boolean)
        .join(' ')}
      onClick={onOpenHistory}
      title={`${text} · ${when}`}
      aria-label={`${text}. ${when}. ${t('prices.action.history')}`}
    >
      <span className={styles.icon}>
        <DirectionIcon direction={delta.direction} />
      </span>
      <span className={styles.text}>{text}</span>
      {percent !== '' && <span className={`mono ${styles.percent}`}>{percent}</span>}
      <span className={styles.when}>{when}</span>
    </button>
  );
}
